import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';

import useDebounceValue from '@/hooks/useDebounceValue';
import { useBankFeedRouteContext } from '@/pages/company/BankFeedRoute/context';
import { computeEmptyVariant } from '@/pages/company/BankFeedRoute/components/EmptyStates/computeEmptyVariant';

const SEARCH_WIDTH = 255;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 2;

// Full-text search — shared by both per-tab toolbars. Debounced (300ms), activating at
// ≥2 characters. Owns its local input state and resyncs when the parent resets
// `toolbarFilters.search` externally (tab switch / future Reset-filters). Reads the active
// tab from context, so it is identical on both tabs.
const SearchFilter: React.FC = () => {
  const intl = useIntl();
  const { toolbarFilters, setToolbarFilters, accountType, summary } = useBankFeedRouteContext();

  // Pre-data empty state: blank + disabled (mirrors Currency / Date range).
  const isEmptyView = computeEmptyVariant(summary, accountType) !== null;

  const [searchInput, setSearchInput] = useState<string>(toolbarFilters.search);
  const debouncedSearch = useDebounceValue(searchInput, SEARCH_DEBOUNCE_MS) as string;

  // Resync local input when the parent resets `toolbarFilters.search` externally
  // (e.g. a future Reset-filters action or restored state on navigation).
  useEffect(() => {
    setSearchInput((current) => (current === toolbarFilters.search ? current : toolbarFilters.search));
  }, [toolbarFilters.search]);

  useEffect(() => {
    const next = debouncedSearch.length >= SEARCH_MIN_CHARS ? debouncedSearch : '';
    setToolbarFilters((prev) => (prev.search === next ? prev : { ...prev, search: next }));
  }, [debouncedSearch, setToolbarFilters]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  }, []);

  const searchPlaceholder = intl.formatMessage({
    id: 'company.bankFeed.transactions.toolbar.search.placeholder',
    defaultMessage: 'Search',
  });

  return (
    <TextField
      size="small"
      placeholder={searchPlaceholder}
      value={isEmptyView ? '' : searchInput}
      onChange={handleSearchChange}
      disabled={isEmptyView}
      sx={{
        width: SEARCH_WIDTH,
        backgroundColor: 'common.white',
        '& .MuiInputBase-input': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
        // Darker placeholder only while enabled — the disabled one keeps MUI's muted default.
        '& .MuiInputBase-root:not(.Mui-disabled) .MuiInputBase-input::placeholder': {
          color: 'text.secondary',
          opacity: 1,
        },
      }}
      // A native `title`, not a MUI Tooltip: on an editable field the hover that would open a
      // tooltip is the same gesture that focuses it, so the popper covers the click target.
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color={isEmptyView ? 'disabled' : 'action'} />
            </InputAdornment>
          ),
        },
        htmlInput: { 'aria-label': searchPlaceholder, title: isEmptyView ? '' : searchInput },
      }}
    />
  );
};

export default SearchFilter;
